import { useState } from "react";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { date, money, serviceLabels } from "@/lib/format";
import type { Paged, Shipment } from "@/lib/types";
import { Button, Card, EmptyState, PageHeader, Skeleton, StatusBadge, cx } from "@/components/ui";

const FILTERS = [
  { label: "All", value: "" },
  { label: "Open", value: "booked,awaiting_collection,collected,in_transit,at_hub,out_for_delivery" },
  { label: "Delivered", value: "delivered" },
  { label: "Needs attention", value: "exception,attempt_failed,collection_failed" },
];

const PAGE_SIZE = 20;

export function Shipments() {
  const [status, setStatus] = useState("");
  const [search, setSearch] = useState("");
  const [offset, setOffset] = useState(0);

  const query = new URLSearchParams({ limit: String(PAGE_SIZE), offset: String(offset) });
  if (status) query.set("status", status);
  if (search.trim()) query.set("q", search.trim());

  const { data, isLoading } = useQuery({
    queryKey: ["shipments", status, search, offset],
    queryFn: () => api.get<Paged<Shipment>>(`/v1/shipments?${query.toString()}`),
    placeholderData: keepPreviousData,
  });

  const total = data?.meta.total ?? 0;

  return (
    <>
      <PageHeader
        title="Shipments"
        subtitle={total ? `${total} shipment${total === 1 ? "" : "s"}` : undefined}
        action={
          <Link to="/app/book">
            <Button>Book a shipment</Button>
          </Link>
        }
      />

      <div className="mb-4 flex flex-wrap items-center gap-2">
        {FILTERS.map((filter) => (
          <button
            key={filter.label}
            onClick={() => {
              setStatus(filter.value);
              setOffset(0);
            }}
            className={cx(
              "rounded-lg px-3 py-1.5 text-sm font-medium transition",
              status === filter.value ? "bg-ink-900 text-white" : "bg-white text-ink-600 hover:bg-ink-200/60",
            )}
          >
            {filter.label}
          </button>
        ))}
        <input
          value={search}
          onChange={(event) => {
            setSearch(event.target.value);
            setOffset(0);
          }}
          placeholder="Waybill, reference or city"
          className="ws-input ml-auto w-full sm:w-72"
          aria-label="Search shipments"
        />
      </div>

      {isLoading ? (
        <Card>
          <Skeleton className="h-64 w-full" />
        </Card>
      ) : !data?.data.length ? (
        <EmptyState
          title="Nothing here"
          body={search || status ? "No shipments match that filter." : "Book your first shipment to get started."}
        />
      ) : (
        <>
          <div className="ws-card overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="border-b border-ink-200 bg-ink-100/60 text-xs uppercase tracking-wide text-ink-400">
                <tr>
                  <th className="px-5 py-3">Waybill</th>
                  <th className="px-5 py-3">Route</th>
                  <th className="hidden px-5 py-3 md:table-cell">Service</th>
                  <th className="px-5 py-3">Status</th>
                  <th className="px-5 py-3 text-right">Price</th>
                  <th className="hidden px-5 py-3 text-right sm:table-cell">Booked</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-ink-200">
                {data.data.map((shipment) => (
                  <tr key={shipment.id} className="transition hover:bg-ink-100/60">
                    <td className="px-5 py-3">
                      <Link to={`/app/shipments/${shipment.id}`} className="font-mono text-xs font-medium text-brand">
                        {shipment.waybill}
                      </Link>
                      {shipment.test_mode && <span className="ml-2 text-[10px] uppercase text-ink-400">test</span>}
                    </td>
                    <td className="px-5 py-3">
                      {shipment.collection.city} to {shipment.delivery.city}
                    </td>
                    <td className="hidden px-5 py-3 text-ink-600 md:table-cell">{serviceLabels[shipment.service_level] ?? shipment.service_level}</td>
                    <td className="px-5 py-3">
                      <StatusBadge status={shipment.status} />
                    </td>
                    <td className="px-5 py-3 text-right font-medium">{money(shipment.price_cents)}</td>
                    <td className="hidden px-5 py-3 text-right text-ink-400 sm:table-cell">{date(shipment.created_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {total > PAGE_SIZE && (
            <div className="mt-4 flex items-center justify-between text-sm text-ink-400">
              <span>
                {offset + 1} to {Math.min(offset + PAGE_SIZE, total)} of {total}
              </span>
              <div className="flex gap-2">
                <Button size="sm" variant="secondary" disabled={offset === 0} onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}>
                  Previous
                </Button>
                <Button
                  size="sm"
                  variant="secondary"
                  disabled={offset + PAGE_SIZE >= total}
                  onClick={() => setOffset(offset + PAGE_SIZE)}
                >
                  Next
                </Button>
              </div>
            </div>
          )}
        </>
      )}
    </>
  );
}
